import React, { useState } from 'react';
import Slider from '@mui/material/Slider';
import Switch from '@mui/material/Switch';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

const FundSimulator = ({ index, fund, updateFund, removeFund }) => {
  const [fundValue, setFundValue] = useState(fund.fundValue || 500);
  const [returnRate, setReturnRate] = useState(fund.returnRate || 20);
  const [ipca, setIpca] = useState(fund.ipca || 4);
  const [hurdleRate, setHurdleRate] = useState(fund.hurdleRate || 4);
  const [carryRate, setCarryRate] = useState(fund.carryRate || 20);
  const [managementFee, setManagementFee] = useState(fund.managementFee || 2);
  const [isCatchUp, setIsCatchUp] = useState(fund.isCatchUp || false);
  const [isActive, setIsActive] = useState(fund.isActive || false);

  const handleFundValueChange = (event, newValue) => {
    setFundValue(newValue);
    updateFund(index, { fundValue: newValue, returnRate, ipca, hurdleRate, carryRate, managementFee, isCatchUp, isActive });
  };

  const handleReturnRateChange = (event, newValue) => {
    setReturnRate(newValue);
    updateFund(index, { fundValue, returnRate: newValue, ipca, hurdleRate, carryRate, managementFee, isCatchUp, isActive });
  };

  const handleIpcaChange = (event) => {
    const newValue = Number(event.target.value);
    setIpca(newValue);
    updateFund(index, { fundValue, returnRate, ipca: newValue, hurdleRate, carryRate, managementFee, isCatchUp, isActive });
  };

  const handleHurdleRateChange = (event, newValue) => {
    setHurdleRate(newValue);
    updateFund(index, { fundValue, returnRate, ipca, hurdleRate: newValue, carryRate, managementFee, isCatchUp, isActive });
  };

  const handleCarryRateChange = (event, newValue) => {
    setCarryRate(newValue);
    updateFund(index, { fundValue, returnRate, ipca, hurdleRate, carryRate: newValue, managementFee, isCatchUp, isActive });
  };

  const handleManagementFeeChange = (event) => {
    const newValue = Number(event.target.value);
    setManagementFee(newValue);
    updateFund(index, { fundValue, returnRate, ipca, hurdleRate, carryRate, managementFee: newValue, isCatchUp, isActive });
  };

  const handleIsCatchUpChange = (event) => {
    setIsCatchUp(event.target.checked);
    updateFund(index, { fundValue, returnRate, ipca, hurdleRate, carryRate, managementFee, isCatchUp: event.target.checked, isActive });
  };

  const handleIsActiveChange = (event) => {
    setIsActive(event.target.checked);
    updateFund(index, { fundValue, returnRate, ipca, hurdleRate, carryRate, managementFee, isCatchUp, isActive: event.target.checked });
  };

  return (
    <Card sx={{ mb: 4 }}>
      <CardHeader
        title={`Fundo ${index + 1}`}
        action={
          <Button variant="contained" color="error" size="small" onClick={() => removeFund(index)}>
            Remover
          </Button>
        }
      />
      <CardContent>
        <div style={{ marginBottom: '16px' }}>
          <TextField
            label="Valor do Fundo (M)"
            type="number"
            value={fundValue}
            onChange={(e) => handleFundValueChange(e, Number(e.target.value))}
            fullWidth
            margin="normal"
          />
          <Slider
            value={fundValue}
            onChange={handleFundValueChange}
            min={0}
            max={1000}
            step={10}
            valueLabelDisplay="auto"
            sx={{ mb: 4 }}
          />
        </div>
        <div style={{ marginBottom: '16px' }}>
          <TextField
            label="Taxa de Retorno (%)"
            type="number"
            value={returnRate}
            onChange={(e) => handleReturnRateChange(e, Number(e.target.value))}
            fullWidth
            margin="normal"
          />
          <Slider
            value={returnRate}
            onChange={handleReturnRateChange}
            min={0}
            max={50}
            step={1}
            valueLabelDisplay="auto"
            sx={{ mb: 4 }}
          />
        </div>
        <div style={{ marginBottom: '16px' }}>
          <TextField
            label="IPCA (%)"
            type="number"
            value={ipca}
            onChange={handleIpcaChange}
            fullWidth
            margin="normal"
          />
        </div>
        <div style={{ marginBottom: '16px' }}>
          <Slider
            value={hurdleRate}
            onChange={handleHurdleRateChange}
            min={0}
            max={20}
            step={0.5}
            valueLabelDisplay="auto"
          />
        </div>
        <div style={{ marginBottom: '16px' }}>
          <Slider
            value={carryRate}
            onChange={handleCarryRateChange}
            min={0}
            max={50}
            step={1}
            valueLabelDisplay="auto"
          />
        </div>
        <div style={{ marginBottom: '16px' }}>
          <TextField
            label="Taxa Anual do Gestor (%)"
            type="number"
            value={managementFee}
            onChange={handleManagementFeeChange}
            fullWidth
            margin="normal"
          />
        </div>
        <div style={{ marginBottom: '16px' }}>
          <label>Catch-up Provision</label>
          <Checkbox checked={isCatchUp} onChange={handleIsCatchUpChange} />
        </div>
        <div>
          <label>Fundo Ativo</label>
          <Switch checked={isActive} onChange={handleIsActiveChange} />
        </div>
      </CardContent>
    </Card>
  );
};

const FundSimulatorApp = () => {
  const [funds, setFunds] = useState([{ fundValue: 500, returnRate: 20, ipca: 4, hurdleRate: 4, carryRate: 20, managementFee: 2, isCatchUp: false, isActive: true }]);
  const [tabIndex, setTabIndex] = useState(0);
  const [years, setYears] = useState(12);

  const addFund = () => {
    setFunds([...funds, { fundValue: 500, returnRate: 20, ipca: 4, hurdleRate: 4, carryRate: 20, managementFee: 2, isCatchUp: false, isActive: false }]);
  };

  const updateFund = (index, updatedFund) => {
    const updatedFunds = [...funds];
    updatedFunds[index] = updatedFund;
    setFunds(updatedFunds);
  };

  const removeFund = (index) => {
    const updatedFunds = funds.filter((_, i) => i !== index);
    setFunds(updatedFunds);
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleYearsChange = (event) => {
    setYears(Number(event.target.value));
  };

  const calculateResults = (fund) => {
    const hurdleRateTotal = fund.ipca + fund.hurdleRate;
    const finalInvestmentValue = fund.fundValue * Math.pow(1 + fund.returnRate / 100, years);
    const totalManagementFee = fund.fundValue * (fund.managementFee / 100) * years;
    const finalValueAfterFees = finalInvestmentValue - totalManagementFee;
    const priorityReturn = fund.fundValue * Math.pow(1 + hurdleRateTotal / 100, years);
    const carry = fund.carryRate / 100 * (finalValueAfterFees - priorityReturn);
    const distributionToLP = finalValueAfterFees - carry;
    return {
      hurdleRateTotal,
      finalInvestmentValue,
      totalManagementFee,
      finalValueAfterFees,
      priorityReturn,
      carry,
      distributionToLP
    };
  };

  return (
    <div>
      <Tabs value={tabIndex} onChange={handleTabChange} sx={{ mb: 4 }}>
        <Tab label="Simulador de Fundos" />
        <Tab label="Resultados" />
        <Tab label="Comparação" />
      </Tabs>
      {tabIndex === 0 && (
        <div>
          <TextField
            label="Número de Anos"
            type="number"
            value={years}
            onChange={handleYearsChange}
            fullWidth
            margin="normal"
          />
          {funds.map((fund, index) => (
            <FundSimulator
              key={index}
              index={index}
              fund={fund}
              updateFund={updateFund}
              removeFund={removeFund}
            />
          ))}
          <Button variant="contained" color="primary" onClick={addFund} sx={{ mt: 2 }}>
            Adicionar Fundo
          </Button>
        </div>
      )}
      {tabIndex === 1 && (
        <div>
          <h2>Resultados (após {years} anos)</h2>
          {funds.map((fund, index) => {
            const results = calculateResults(fund);
            return (
              <div key={index}>
                <h3>Fundo {index + 1}</h3>
                <p>Valor Final do Investimento: {results.finalInvestmentValue.toFixed(2)}M</p>
                <p>Taxa Total do Gestor: {results.totalManagementFee.toFixed(2)}M</p>
                <p>Priority Return: {results.priorityReturn.toFixed(2)}M</p>
                <p>Carry: {results.carry.toFixed(2)}M</p>
                <p>Distribuição para LP: {results.distributionToLP.toFixed(2)}M</p>
              </div>
            );
          })}
        </div>
      )}
      {tabIndex === 2 && (
        <LineChart
          width={600}
          height={300}
          data={funds.map((fund, index) => ({ name: `Fundo ${index + 1}`, value: fund.fundValue }))}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey="value" stroke="#8884d8" />
        </LineChart>
      )}
    </div>
  );
};

export default FundSimulatorApp;


// Add the following export to make it work with App.js
export { FundSimulatorApp as VCCarrySimulator };

